import { type Range, useVirtualizer } from '@tanstack/react-virtual'
import { useCallback } from 'react'
import type {
  DataTableInstance,
  DataTableRow,
  DataTableRowData,
  DataTableRowVirtualizer,
} from '../types'
import { parseFromValuesOrFunc } from '../utils/utils'
import { extraIndexRangeExtractor } from '../utils/virtualization.utils'

export const useTableRowVirtualizer = <
  TData extends DataTableRowData,
  TScrollElement extends Element | Window = HTMLDivElement,
  TItemElement extends Element = HTMLTableRowElement,
>(
  table: DataTableInstance<TData>,
  rows?: DataTableRow<TData>[],
): DataTableRowVirtualizer<TScrollElement, TItemElement> | undefined => {
  const {
    getRowModel,
    getState,
    options: {
      enableRowVirtualization,
      renderDetailPanel,
      rowVirtualizerInstanceRef,
      rowVirtualizerOptions,
    },
    refs: { tableContainerRef },
  } = table
  const { draggingRow, expanded } = getState()

  if (!enableRowVirtualization) return undefined

  const rowVirtualizerProps = parseFromValuesOrFunc(rowVirtualizerOptions, {
    table,
  })

  const rowCount = rows?.length ?? getRowModel().rows.length

  const normalRowHeight = 56

  const rowVirtualizer = useVirtualizer({
    count: renderDetailPanel ? rowCount * 2 : rowCount,
    estimateSize: (index) => {
      if (renderDetailPanel && index % 2 === 1) {
        if (expanded === true) {
          return 100
        }
        return 0
      }
      return normalRowHeight
    },
    getScrollElement: () => tableContainerRef.current,
    measureElement:
      typeof window !== 'undefined' && navigator.userAgent.indexOf('Firefox') === -1
        ? (element) => element?.getBoundingClientRect().height
        : undefined,
    overscan: 4,
    rangeExtractor: useCallback(
      (range: Range) => extraIndexRangeExtractor(range, draggingRow?.index ?? 0),
      [draggingRow],
    ),
    ...rowVirtualizerProps,
  }) as unknown as DataTableRowVirtualizer<TScrollElement, TItemElement>

  // biome-ignore lint/suspicious/noExplicitAny: TODO - fix type definition
  rowVirtualizer.virtualRows = rowVirtualizer.getVirtualItems() as any

  if (rowVirtualizerInstanceRef) {
    // @ts-expect-error reference for virtualised row
    rowVirtualizerInstanceRef.current = rowVirtualizer
  }

  return rowVirtualizer
}
